import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import dateFormat from 'dateformat';
import { Col, Button } from 'reactstrap';
import { SOURCES } from 'client/utils/image-helpers';
import { Link } from 'site-modules/shared/components/link/link';
import { Image } from 'site-modules/shared/components/image/image';
import { getMakeModelLinkUrl } from 'site-modules/shared/utils/vehicle-link-constructor';

import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';

import { COLORS_AND_OPTIONS, CONDITION_AND_MILEAGE } from 'site-modules/shared/constants/appraisal/appraisal';

import './appraisal-report-step-head.scss';

/**
 * Renders top of report (photo, color, mileage, condition, mmy and style, total value)
 * @param {String} title
 * @param {String} make
 * @param {String} model
 * @param {String} style
 * @param {String} photo
 * @param {Object[]} descriptionValues
 * @param {Boolean} isExtendedReport
 * @param {Boolean} showExteriorColor
 * @param {Boolean} showAddEquipment
 * @returns {ReactElement}
 */
export function AppraisalReportStepHead({
  title = '',
  make = '',
  model = '',
  style = '',
  photo = '',
  descriptionValues = [],
  isExtendedReport = false,
  showExteriorColor = true,
  showAddEquipment = false,
  changeStep,
}) {
  const [today] = useState(() => dateFormat(Date.now(), 'longDate'));
  const makeModelLinkUrl = getMakeModelLinkUrl({
    makeSlug: make,
    modelSlug: model,
  });

  const editLinkClassName =
    'border-0 p-0 text-transform-none fw-normal text-primary-darker btn-link text-start edit-link';

  const editLinkDataByDescriptionTitle = {
    Exterior: {
      stepId: COLORS_AND_OPTIONS,
      trackingId: 'edit_color',
    },
    Mileage: {
      stepId: CONDITION_AND_MILEAGE,
      trackingId: 'edit_mileage',
    },
  };

  const renderEditLink = descriptionTitle => {
    const editLinkData = editLinkDataByDescriptionTitle[descriptionTitle];
    if (editLinkData) {
      return (
        <span className="ps-0_5 size-14">
          (
          <Button
            onClick={changeStep}
            className={editLinkClassName}
            data-tracking-id={editLinkData.trackingId}
            data-index={editLinkData.stepId}
          >
            edit
          </Button>
          )
        </span>
      );
    }
    return null;
  };

  return (
    <Fragment>
      <Col xs={12}>
        <div className="heading-4">{title}</div>
        <div className="text-gray-darker">{style}</div>
      </Col>
      <Col xs={12} md={isExtendedReport ? 5 : 6}>
        <Link
          className={classnames('d-block my-0_75 text-center', {
            'text-md-start': isExtendedReport,
          })}
          to={makeModelLinkUrl}
          target="_blank"
        >
          <Image
            imageId={photo}
            className={classnames('img-fluid', { rounded: isExtendedReport })}
            alt={title}
            source={SOURCES.MEDIA}
          />
        </Link>
      </Col>
      {isExtendedReport && (
        <Col xs={12} md={7} className="pt-md-0_5">
          {descriptionValues.map(
            ({ title: valueTitle, value, isTotal }) =>
              (valueTitle !== 'Exterior' || (valueTitle === 'Exterior' && showExteriorColor)) && (
                <div key={valueTitle} className="text-info pb-0_5">
                  <span>{valueTitle}:</span>
                  <span className="ps-0_25">
                    {isTotal ? (
                      <Fragment>
                        {formatPriceStringV2(value)}
                        <div className="small">As of {today}</div>
                      </Fragment>
                    ) : (
                      value
                    )}
                  </span>
                  {renderEditLink(valueTitle)}
                </div>
              )
          )}
          {showAddEquipment && (
            <Button
              onClick={changeStep}
              className={editLinkClassName}
              data-tracking-id="edit_options"
              data-index={COLORS_AND_OPTIONS}
            >
              <span className="icon-plus-circle2 me-0_5" aria-hidden />
              Add additional equipment to my appraisal
            </Button>
          )}
        </Col>
      )}
    </Fragment>
  );
}

AppraisalReportStepHead.propTypes = {
  changeStep: PropTypes.func.isRequired,
  title: PropTypes.string,
  make: PropTypes.string,
  model: PropTypes.string,
  style: PropTypes.string,
  photo: PropTypes.string,
  descriptionValues: PropTypes.arrayOf(PropTypes.shape({})),
  isExtendedReport: PropTypes.bool,
  showExteriorColor: PropTypes.bool,
  showAddEquipment: PropTypes.bool,
};
